<template>
  <div :class="componentClass.root">
    <PmLoadingPure v-if="isLoading" size="default" />

    <template v-if="!isLoading">
      <div :class="componentClass.element('content')">
        <PmKeyValueListPure>
          <PmKeyValuePure v-if="IS_DEVELOPMENT" label="Id" variant="debug">
            {{ id }}
          </PmKeyValuePure>

          <PmKeyValuePure label="Ressource">
            {{ resource }}
          </PmKeyValuePure>

          <PmKeyValuePure label="Typ">
            {{ type }}
          </PmKeyValuePure>

          <PmKeyValuePure label="Anzahl">
            {{ quantity }}
          </PmKeyValuePure>

          <PmKeyValuePure label="Start/Ende">
            <template v-if="props.startDate && props.endDate">
              {{ startEndDateForText(props.startDate, props.endDate) }}
            </template>
          </PmKeyValuePure>

          <PmKeyValuePure v-if="isRegularJobAllocation" label="Projekt">
            <div
              v-if="project?.id && project?.status && project?.number"
              :class="componentClass.element('project')"
            >
              <PmStatusPillPure
                type="project"
                :label="project.number"
                :project-status="project.status"
              />

              <PmLinkPure
                v-if="project.label"
                :label="project.label"
                @click="
                  project?.id ? emit('openProject', project.id) : undefined
                "
              />
            </div>
          </PmKeyValuePure>

          <PmKeyValuePure v-if="stockTypeService" label="Werkstattvorgang">
            <div :class="componentClass.element('stockTypeService')">
              <PmPillPure :label="stockTypeService.number" />

              <PmLinkPure
                v-if="stockTypeService.label"
                :label="stockTypeService.label"
                @click="emit('openStockTypeService')"
              />
            </div>
          </PmKeyValuePure>

          <PmKeyValuePure v-if="purchaseOrder" label="Bestellung">
            <div :class="componentClass.element('purchaseOrder')">
              <PmPillPure :label="purchaseOrder.number" />

              <PmLinkPure
                v-if="purchaseOrder.label"
                :label="purchaseOrder.label"
                @click="emit('openPurchaseOrder')"
              />
            </div>
          </PmKeyValuePure>

          <PmKeyValuePure v-if="isRegularJobAllocation" label="Jobs">
            <div
              v-if="job?.id && job?.status && job?.number"
              :class="componentClass.element('job')"
            >
              <PmStatusPillPure
                type="job"
                :label="job.number"
                :job-status="job.status"
              />

              <PmLinkPure
                v-if="job.title"
                :label="job.title"
                @click="emit('openJob', job.id)"
              />
            </div>
          </PmKeyValuePure>

          <PmKeyValuePure label="Ressourcen-Zuordnungen">
            <ul>
              <li
                v-for="resourceAllocation in props.resourceAllocations"
                :key="resourceAllocation.id"
              >
                <div :class="componentClass.element('resourceAllocation')">
                  <PmResourceAllocationStatusPure
                    v-if="resourceAllocation.status"
                    :status="resourceAllocation.status"
                  />

                  <PmLinkPure
                    v-if="resourceAllocation.name"
                    :label="resourceAllocation.name"
                    @click="
                      emit('openResourceAllocation', resourceAllocation.id)
                    "
                  />
                </div>
              </li>
            </ul>
          </PmKeyValuePure>

          <PmKeyValuePure label="Erstellt">
            <template v-if="props.createdDate && props.createdBy">
              {{ formatToRelativeDate(props.createdDate) }}
              von {{ props.createdBy }}
            </template>
          </PmKeyValuePure>

          <PmKeyValuePure label="Zuletzt geändert">
            <template v-if="props.editedDate && props.editedBy">
              {{ formatToRelativeDate(props.editedDate) }}
              von {{ props.editedBy }}
            </template>
          </PmKeyValuePure>
        </PmKeyValueListPure>

        <div :class="componentClass.element('note')">
          <slot name="note" />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, type VNode } from 'vue'
import { useComponentClass } from '@thomasaull-shared/composables'
import PmKeyValueListPure from '@/components/basics/PmKeyValueList/PmKeyValueListPure.vue'
import PmKeyValuePure from '@/components/basics/PmKeyValueList/PmKeyValuePure.vue'
import PmPillPure from '@/components/basics/PmPillPure.vue'
import PmStatusPillPure from '@/components/persoplan/PmStatusPill/PmStatusPillPure.vue'
import PmLinkPure from '@/components/basics/PmLink/PmLinkPure.vue'
import PmLoadingPure from '@/components/basics/PmLoading/PmLoadingPure.vue'
import PmAddressPure, {
  type Props as PropsAddressPure,
} from '@/components/basics/PmAddress/PmAddressPure.vue'
import { IS_DEVELOPMENT } from '@/constants/general'
import { startEndDateForText, formatToRelativeDate } from '@/utilities/date'
import type { Nilable } from '@/types/misc'
import type {
  ProjectStatus,
  JobStatus,
  ResourceAllocationStatus,
} from '@/constants/persoplan'
import PmResourceAllocationStatusPure from '@/components/persoplan/PmResourceAllocation/PmResourceAllocationStatusPure.vue'

export interface Props {
  id: number
  isLoading?: boolean
  resource?: Nilable<string>
  type?: Nilable<string>
  quantity?: Nilable<number>
  startDate?: Nilable<Date>
  endDate?: Nilable<Date>
  createdDate?: Nilable<Date>
  createdBy?: Nilable<string>
  editedDate?: Nilable<Date>
  editedBy?: Nilable<string>
  project?: Nilable<Project>
  stockTypeService?: Nilable<StockTypeService>
  purchaseOrder?: Nilable<PurchaseOrder>
  job?: Nilable<Job>
  resourceAllocations?: ResourceAllocation[]
}

interface Project {
  id: number
  number: Nilable<string>
  label: Nilable<string>
  status: Nilable<ProjectStatus>
}

interface StockTypeService {
  id: number
  number: Nilable<string>
  label: Nilable<string>
}

interface PurchaseOrder {
  id: number
  number: Nilable<string>
  label: Nilable<string>
}

interface Job {
  id: number
  number: Nilable<string>
  title: Nilable<string>
  status: Nilable<JobStatus>
}

export type ResourceAllocation = {
  id: number
  name: Nilable<string>
  status: Nilable<ResourceAllocationStatus>
}

const props = withDefaults(defineProps<Props>(), {})

/**
 * A resource allocation that belongs directly to a Job, rather than to a
 * StockTypeService or PurchaseOrder. It keeps Job-derived rows visible even
 * when their data is absent.
 */
const isRegularJobAllocation = computed(() => {
  if (props.stockTypeService) return false
  if (props.purchaseOrder) return false

  return true
})

export type Emits = {
  openProject: [id: number]
  openStockTypeService: []
  openPurchaseOrder: []
  openJob: [id: number]
  openResource: [id: number]
  openResourceAllocation: [id: number]
}

const emit = defineEmits<Emits>()

export type Slots = {
  note: () => VNode[]
}

const slots = defineSlots<Slots>()

const componentClass = useComponentClass({ props })
</script>

<style lang="scss" scoped>
.PmDataModalResourcePure {
  $block: &;

  &-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &-project {
    display: flex;
    gap: 8px;
  }

  &-job {
    display: flex;
    gap: 8px;
  }

  &-stockTypeService {
    display: flex;
    gap: 8px;
  }

  &-purchaseOrder {
    display: flex;
    gap: 8px;
  }

  &-resourceAllocation {
    display: flex;
    gap: 4px;
  }
}
</style>
